import axios, { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

import { store } from '../store/store';
import { hide, show } from '../store/features/loader';
import { NUMBERS } from '../resources/number-constants';
import { APIS_ENDPOINTS, API_ERROR } from '../resources/api-constants';
import eventBus from './eventBus';
import { arrayToObject, isAuth, getAccessToken, clearAllLocalStorage, setDefaultAuthToken, renderErrorToast } from './helpers';
import { clearTokens } from './tokenStorage';
import { refreshTokens } from '../auth/tokenService';
import { clearTokenRefreshSchedule, scheduleTokenRefresh } from '../auth/refreshScheduler';

function onRequest(config: any): any {
    if (config?.isLoader) {
        store.dispatch(show());
    }
    const token = getAccessToken();
    if (token) {
        config.headers.Authorization = token;
    }
    return config;
}

const onResponse = (response: any): AxiosResponse => {
    if (response.config?.isLoader) {
        store.dispatch(hide());
    }
    return response;
};

const onRequestError = (error: AxiosError): Promise<AxiosError> => {
    return Promise.reject(error);
};

let lastAuthErrorHandledAt = 0;

const handleAuthError = () => {
    const now = Date.now();
    const shouldNotify = now - lastAuthErrorHandledAt >= 2000;

    // Always run auth cleanup, even for rapid duplicate failures.
    // Cleanup operations are idempotent and keep client auth state consistent.
    clearAllLocalStorage();
    clearTokens();
    clearTokenRefreshSchedule();
    setDefaultAuthToken(axios, null);
    eventBus.dispatch('logout');

    // Deduplicate only the user-facing toast to avoid noisy bursts.
    if (shouldNotify) {
        lastAuthErrorHandledAt = now;
        renderErrorToast('Your session has expired. Please log in again to continue.');
    }
};

// Flag to prevent multiple simultaneous refresh requests
let isRefreshing = false;
// Queue for failed requests during token refresh
type QueuedRequest = {
    resolve: (value: string) => void;
    reject: (error?: any) => void;
};
let failedQueue: QueuedRequest[] = [];

/**
 * Process queued requests after token refresh
 */
const processQueue = (error: AxiosError | null, token: string | null = null): void => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else if (token) {
            prom.resolve(token);
        } else {
            prom.reject(new Error('Token refresh failed: No token received'));
        }
    });
    failedQueue = [];
};

const onResponseError = async (error: AxiosError): Promise<AxiosError | AxiosResponse> => {
    store.dispatch(hide());
    
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    
    // Handle 401 Unauthorized errors - attempt token refresh
    if (error?.response?.status === API_ERROR.FOUR_ZERO_ONE && originalRequest && !originalRequest._retry) {
        // Don't try to refresh if this is the refresh endpoint itself or logout endpoint
        const requestUrl = originalRequest.url || '';
        const refreshEndpoint = APIS_ENDPOINTS.TOKEN_REFRESH.replace(/^\//, ''); // Remove leading slash for comparison
        const logoutEndpoint = APIS_ENDPOINTS.AUTH_LOGOUT.replace(/^\//, ''); // Remove leading slash for comparison
        
        if (requestUrl.includes(refreshEndpoint) || requestUrl.includes('/api/token/refresh')) {
            handleAuthError();
            return Promise.reject(error);
        }
        
        // Skip token refresh for logout endpoint - logout should work even with expired tokens
        // and we already handle errors gracefully by clearing local storage
        if (requestUrl.includes(logoutEndpoint) || requestUrl.includes('/auth/logout')) {
            return Promise.reject(error);
        }

        // If already refreshing, queue this request
        if (isRefreshing) {
            return new Promise<string>((resolve, reject) => {
                failedQueue.push({ resolve, reject });
            })
                .then((token: string) => {
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    return axios(originalRequest);
                })
                .catch((err) => {
                    return Promise.reject(err);
                });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
            await refreshTokens();
            const newToken = getAccessToken()?.replace('Bearer ', '');
            if (!newToken) {
                throw new Error('Token refresh failed: No token received');
            }
            processQueue(null, newToken);
            scheduleTokenRefresh();
            
            // Retry original request with new token
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return axios(originalRequest);
        } catch (refreshError) {
            processQueue(refreshError as AxiosError, null);
            handleAuthError();
            return Promise.reject(refreshError);
        } finally {
            isRefreshing = false;
        }
    }
    
    // Handle other error statuses
    if (error?.response?.status === NUMBERS.FIVE_HUNDRED_TWO || error?.code === 'ERR_NETWORK') {
        // Handle 502 Bad Gateway error
        renderErrorToast('Oops! Something went wrong with the server. Please try again later.');
    } else if (error?.response?.status === NUMBERS.FIVE_HUNDRED_THREE) {
        // Handle 503 Service Unavailable error
        renderErrorToast('Oops! The service is currently unavailable. Please try again later.');
    } else if (error?.response?.status === NUMBERS.FIVE_HUNDRED_FOUR) {
        // Handle 504 Gateway Timeout error
        renderErrorToast('Oops! The request took too long to process. Please try again.');
    } else if (isAuth(error) && error?.response?.status !== API_ERROR.FOUR_ZERO_ONE) {
        // Handle 403 Forbidden (but not 401, which is handled above)
        handleAuthError();
    }
    
    return Promise.reject(error);
};

export function setupInterceptorsTo(
    axiosInstance: AxiosInstance,
): AxiosInstance {
    axiosInstance.defaults.baseURL = `${process.env.REACT_APP_API_URL}/`;
    axiosInstance.defaults.headers.common.Authorization = null;
    axiosInstance.defaults.headers.patch['Content-Type'] = 'application/json';
    axiosInstance.defaults.transformResponse = [
        (data: any, res: any) => {
            if (res['content-type'] !== 'application/pdf' && typeof data === 'string') {
                data = JSON?.parse(data);
                if (Array.isArray(data)) {
                    data = arrayToObject(data);
                }
            }
            return data;
        },
    ];
    axiosInstance.interceptors.request.use(onRequest, onRequestError);
    axiosInstance.interceptors.response.use(onResponse, onResponseError);
    return axiosInstance;
}
